import React, { useState, useEffect } from 'react';
import HybridCreditService from '../services/hybridCreditService';
import Icon from './AppIcon';
import Button from './ui/Button';

const CreditAvailabilityNotification = ({ userId, isPremium, onUpgrade }) => {
  console.log('CreditAvailabilityNotification: Component rendered with props:', { userId, isPremium });
  
  const [creditInfo, setCreditInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchCreditInfo = async () => {
      console.log('CreditAvailabilityNotification: useEffect triggered', { userId, isPremium, refreshKey });
      
      if (!userId) {
        console.log('CreditAvailabilityNotification: No userId, setting loading to false');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('CreditAvailabilityNotification: Fetching credit info for user:', userId, 'isPremium:', isPremium);
        
        // Get credit breakdown
        const breakdownResult = await HybridCreditService.getCreditBreakdown(userId);
        console.log('CreditAvailabilityNotification: Credit breakdown result:', breakdownResult);
        
        if (breakdownResult.success) {
          setCreditInfo(breakdownResult.data);
          console.log('CreditAvailabilityNotification: Set credit info:', breakdownResult.data);
        } else {
          console.error('CreditAvailabilityNotification: Credit breakdown failed:', breakdownResult.error);
          setError(breakdownResult.error);
        }

        // Check assessment eligibility
        const eligibilityResult = await HybridCreditService.canTakeAssessment(userId, isPremium);
        console.log('CreditAvailabilityNotification: Assessment eligibility result:', eligibilityResult);
        console.log('CreditAvailabilityNotification: Eligibility details:', {
          success: eligibilityResult.success,
          canTake: eligibilityResult.canTake,
          reason: eligibilityResult.reason,
          error: eligibilityResult.error
        });
        
        if (eligibilityResult.success) {
          setCreditInfo(prev => {
            const updated = {
              ...prev,
              canTakeAssessment: eligibilityResult.canTake,
              assessmentReason: eligibilityResult.reason
            };
            console.log('CreditAvailabilityNotification: Updated credit info with eligibility:', updated);
            return updated;
          });
        }
      } catch (err) {
        console.error('CreditAvailabilityNotification: Error fetching credit info:', err);
        setError('Failed to load credit information');
      } finally {
        setLoading(false);
      }
    };

    fetchCreditInfo();
  }, [userId, isPremium, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Expired';
    if (diffDays === 1) return 'Expires tomorrow';
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    if (diffDays <= 30) return `Expires in ${Math.ceil(diffDays / 7)} weeks`;
    return `Expires ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-muted rounded w-1/2"></div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleRefresh}
          className="mt-2"
        >
          <Icon name="RefreshCw" size={14} className="mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center space-x-2 text-error mb-2">
          <Icon name="AlertTriangle" size={16} />
          <span className="text-sm">Unable to load credit information</span>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
        >
          <Icon name="RefreshCw" size={14} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  if (!creditInfo) {
    return null;
  }

  const { snapshot, soonestExpiry, canTakeAssessment, assessmentReason } = creditInfo;
  const monthlyCredits = snapshot?.monthly_credits_available || 0;
  const purchasedCredits = snapshot?.purchased_credits_available || 0;
  const totalAvailable = monthlyCredits + purchasedCredits;

  // Determine the display message based on user status
  const getDisplayMessage = () => {
    console.log('CreditAvailabilityNotification: getDisplayMessage called with:', {
      isPremium,
      totalAvailable,
      monthlyCredits,
      purchasedCredits,
      assessmentReason,
      canTakeAssessment
    });

    if (!isPremium) {
      if (assessmentReason === 'free_first') {
        return {
          type: 'success',
          icon: 'CheckCircle',
          title: 'Free Assessment Available',
          message: 'You have 1 free assessment available to get started with your career development.',
          showUpgrade: false
        };
      } else {
        return {
          type: 'warning',
          icon: 'AlertTriangle',
          title: 'No Free Assessments Remaining',
          message: 'You\'ve used your free assessment. Purchase credits or upgrade to Premium for additional assessments.',
          showUpgrade: true
        };
      }
    } else {
      if (totalAvailable > 0) {
        console.log('CreditAvailabilityNotification: Premium user with credits, showing success message');
        return {
          type: 'success',
          icon: 'Coins',
          title: `${totalAvailable} Credit${totalAvailable !== 1 ? 's' : ''} Available`,
          message: `You have ${monthlyCredits} monthly and ${purchasedCredits} purchased credits available.`,
          showUpgrade: false,
          expiryInfo: soonestExpiry ? formatExpiryDate(soonestExpiry) : null
        };
      } else {
        console.log('CreditAvailabilityNotification: Premium user with no credits, showing warning message');
        return {
          type: 'warning',
          icon: 'AlertTriangle',
          title: 'No Credits Available',
          message: 'You\'ve used all your credits. Purchase more credits or wait for your monthly renewal.',
          showUpgrade: true
        };
      }
    }
  };

  const displayInfo = getDisplayMessage();

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${
      displayInfo.type === 'success' ? 'border-success/20 bg-success/5' : 'border-warning/20 bg-warning/5'
    }`}>
      <div className="flex items-start space-x-3">
        <Icon 
          name={displayInfo.icon} 
          size={20} 
          className={`mt-0.5 ${
            displayInfo.type === 'success' ? 'text-success' : 'text-warning'
          }`} 
        />
        <div className="flex-1">
          <h4 className={`font-semibold text-sm mb-1 ${
            displayInfo.type === 'success' ? 'text-success' : 'text-warning'
          }`}>
            {displayInfo.title}
          </h4>
          <p className="text-sm text-muted-foreground mb-2">
            {displayInfo.message}
          </p>
          
          {displayInfo.expiryInfo && (
            <p className="text-xs text-muted-foreground mb-2">
              <Icon name="Clock" size={12} className="inline mr-1" />
              {displayInfo.expiryInfo}
            </p>
          )}

          {displayInfo.showUpgrade && (
            <Button
              variant="outline"
              size="sm"
              onClick={onUpgrade}
              className={`${
                displayInfo.type === 'warning' 
                  ? 'border-warning text-warning hover:bg-warning hover:text-warning-foreground'
                  : 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              <Icon name="Crown" size={14} className="mr-2" />
              {isPremium ? 'Buy Credits' : 'Upgrade to Premium'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditAvailabilityNotification;
